import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { isSome } from 'fp-ts/Option';

import i18n from 'asset/translation';
import { dispatchFormChangesView, useFormChangesView } from 'adapter/in/common/context/formChanges';
import { dispatchCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import { dispatchDashboardEdit, useDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import {
    dispatchWidgetMenuView,
    PANEL,
    PanelType,
    useWidgetMenuView,
    WIDGET_STATUS,
} from 'adapter/in/dashboards/context/EditDashboard/WidgetMenuView';
import PanelMainContainer from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Main';
import PanelDataSetContainer from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/DataSet';
import PanelWidgetConfigurationContainer from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration';
import { useWidgetMenuDiscardChangesCleanupActions } from 'adapter/in/dashboards/containers/Modals/DiscardChangesModal/useDiscardChangesCleanupActions';

// eslint-disable-next-line consistent-return
export const getMenuContent = (panelType: PanelType): ReactElement | void => {
    // eslint-disable-next-line default-case
    switch (panelType) {
        case PANEL.MAIN:
            return <PanelMainContainer />;
        case PANEL.DATA_SET:
            return <PanelDataSetContainer />;
        case PANEL.CONFIGURATION:
            return <PanelWidgetConfigurationContainer />;
    }
};

// eslint-disable-next-line consistent-return
export const getMenuTitle = (panelType: PanelType): string => {
    switch (panelType) {
        default:
        case PANEL.MAIN:
            return i18n.t('widgets.widgetMenu.mainTitle');
        case PANEL.DATA_SET:
            return i18n.t('widgets.widgetMenu.dataSetTitle');
        case PANEL.CONFIGURATION:
            return i18n.t('widgets.widgetMenu.configurationTitle');
    }
};

// eslint-disable-next-line consistent-return
export const getMenuBack = (panelType: PanelType): (() => void) | undefined => {
    const { status } = useWidgetMenuView();
    const { setPanel } = dispatchWidgetMenuView();
    const { reset } = dispatchCurrentWidgetConfiguration();
    const { setHasChange } = dispatchFormChangesView();

    switch (panelType) {
        default:
        case PANEL.MAIN:
            return undefined;
        case PANEL.DATA_SET:
            return () => setPanel(PANEL.MAIN);
        case PANEL.CONFIGURATION:
            return status !== WIDGET_STATUS.EDITING
                ? () => {
                      reset();
                      setHasChange(false);
                      setPanel(PANEL.MAIN);
                  }
                : undefined;
    }
};

const useWidgetMenu = (): {
    title: string;
    content: any;
    onClose: () => void;
    onBack?: () => void;
} => {
    const { resetWidgetMenu } = dispatchWidgetMenuView();
    const { status, panel } = useWidgetMenuView();
    const { hasChange } = useFormChangesView();

    const { dashboard } = useDashboardEdit();
    const { setModalDiscardChanges, setShowSidePanelMenu } = dispatchDashboardEdit();
    const { reset: resetCurrentWidgetConfiguration } = dispatchCurrentWidgetConfiguration();
    const { setHasChange } = dispatchFormChangesView();
    const { closeWidgetMenuCleanupAction } = useWidgetMenuDiscardChangesCleanupActions();
    const onClose = useCallback(() => {
        if (hasChange) {
            if (isSome(dashboard)) setModalDiscardChanges(true, closeWidgetMenuCleanupAction);
        } else {
            resetWidgetMenu();
            resetCurrentWidgetConfiguration();
            setHasChange(false);
            setShowSidePanelMenu(false);
        }
    }, [dashboard, hasChange]);

    useEffect(() => {
        if (status === WIDGET_STATUS.EDITING) {
            setShowSidePanelMenu(true);
        }
    }, [status]);

    const title = useMemo(() => getMenuTitle(panel), [panel]);
    const content = useMemo(() => getMenuContent(panel), [panel]);
    const onBack = getMenuBack(panel);

    return {
        title,
        content,
        onClose,
        onBack,
    };
};

export default useWidgetMenu;
